// frontend/src/components/chatbot/useChatbot.js
import { useCallback } from 'react';
import { useChatbotContext } from './ChatbotContext';
import { httpRequest } from '../../utils/httpClient';
import { API_BASE } from '../../config/api';
import { authService } from '../../services/auth';
import { v4 as uuidv4 } from 'uuid';
import logger from '../../utils/logger';

const CHATBOT_API_URL = `${API_BASE}/api/chat`;

const useChatbot = () => {
  const {
    isOpen,
    toggleOpen,
    openChat,
    closeChat,
    messages,
    addMessage,
    updateLastMessage,
    clearMessages,
    isLoading,
    setIsLoading,
    sessionId,
  } = useChatbotContext();

  const sendMessage = useCallback(
    async (query) => {
      if (!query || !query.trim()) return;

      const trimmed = query.trim();
      const userMsgId = uuidv4();
      addMessage({
        id: userMsgId,
        role: 'user',
        content: trimmed,
        timestamp: new Date(),
      });

      setIsLoading(true);

      const assistantMsgId = uuidv4();
      addMessage({
        id: assistantMsgId,
        role: 'assistant',
        content: '',
        sources: [],
        timestamp: new Date(),
      });

      try {
        const headers = {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders(),
        };

        const response = await httpRequest(`${CHATBOT_API_URL}/query`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ query: trimmed, session_id: sessionId }),
        });

        const data = await response.json();
        updateLastMessage({
          id: assistantMsgId,
          role: 'assistant',
          content: data.answer || 'No response received.',
          sources: data.sources || [],
          query: trimmed,
          timestamp: new Date(),
        });
      } catch (error) {
        logger.error('Chatbot query failed:', error);
        updateLastMessage({
          id: assistantMsgId,
          role: 'assistant',
          content:
            error.status === 429
              ? 'You are sending messages too quickly. Please wait a moment and try again.'
              : 'Sorry, something went wrong. Please try again or email threads@voyagerbug.com.',
          sources: [],
          error: true,
          timestamp: new Date(),
        });
      } finally {
        setIsLoading(false);
      }
    },
    [addMessage, updateLastMessage, setIsLoading, sessionId]
  );

  const clearHistory = useCallback(() => {
    clearMessages();
  }, [clearMessages]);

  const rateMessage = useCallback(
    async (messageId, helpful) => {
      const msg = messages.find((m) => m.id === messageId);
      if (!msg || !msg.query) {
        logger.warn('Cannot rate message without query text', { messageId });
        return;
      }
      try {
        const headers = {
          'Content-Type': 'application/json',
          ...authService.getAuthHeaders(),
        };
        await httpRequest(`${CHATBOT_API_URL}/rate`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            session_id: sessionId,
            query: msg.query,
            helpful,
          }),
        });
      } catch (error) {
        logger.error('Failed to submit rating:', error);
      }
    },
    [messages, sessionId]
  );

  return {
    isOpen,
    toggleOpen,
    openChat,
    closeChat,
    messages,
    isLoading,
    sessionId,
    sendMessage,
    clearHistory,
    rateMessage,
  };
};

export default useChatbot;
